import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { browserHistory } from 'react-router';
import * as actions from '../../actions';
import * as Constants from '../../constants';
import { IMAGE_URL_PREFIX, ROUTE_PATHS } from '../../constants';
import { getServiceByRoutePrefix } from '../../constants/services';
import PatientForm from '@components/PatientForm/PatientForm';
import SlotBooking from './components/SlotBooking';
import FooterButtons from '../../components/Button/FooterButtons';
import DocConsultSuccessModal from '../../components/DocConsultSuccessModal';
import Loader from '@components/Loader';
import './ServiceSlotPatient.scss';

const staticText = {
  heading: 'Book Plan',
  selectSlotFor: 'Select slot for ',
  firstSession: '1st ',
  sessionFallback: 'Session',
  noteLabel: 'Note : ',
  remainingNote: 'The remaining sessions will be planned based on your recovery progress.',
  quickStart: 'Quick start ',
  toRecovery: 'to your recovery!',
  patientDetailsSubtext: 'Add patient details for quick scheduling.',
  backBtn: 'Back to Select Package',
  placingOrder: 'Placing Order...',
  confirmBooking: 'Confirm Booking',
  cancelBtn: 'Cancel Order',
  packageLabel: 'Package:',
};

class ServiceSlotPatient extends Component {
  constructor(props) {
    super(props);
    this.patientFormRef = null;
    this.state = {
      patient: { name: '', gender: '', contact: '', age: '' },
      orderLoading: false,
      orderSuccess: false,
      orderId: null,
      orderError: '',
    };
  }

  setPatientFormRef = (ref) => {
    this.patientFormRef = ref;
  }

  componentDidMount() {
    const { params, actions, servicePlatform } = this.props;
    const { serviceType } = params;
    const { service, selectedPackage } = servicePlatform;

    if (!service || !service.serviceId) {
      const serviceConfig = getServiceByRoutePrefix(serviceType);
      if (serviceConfig) {
        actions.setServiceDetails(serviceConfig);
      }
    }

    if (!selectedPackage) {
      browserHistory.push(ROUTE_PATHS.SERVICE_SELECT_PACKAGE(serviceType));
      return;
    }

    actions.changeHeaderState(Constants.SERVICE_PLATFORM_SLOT_PATIENT);

    // Fetch item details to get vendorId/vendorName and showSlot flag
    actions.fetchServiceItemDetails(selectedPackage.id);
  }

  componentDidUpdate(prevProps) {
    const { servicePlatform, actions } = this.props;
    const prev = prevProps.servicePlatform;

    // Once item details resolve and hasSlots is known, fetch slots
    if (!prev.service.hasSlots && servicePlatform.service.hasSlots) {
      actions.fetchServiceSlots(servicePlatform.service.serviceId);
    }

    if (prev.orderLoading && !servicePlatform.orderLoading) {
      if (servicePlatform.orderId) {
        this.setState({ orderLoading: false, orderSuccess: true, orderId: servicePlatform.orderId });
      } else if (servicePlatform.orderError) {
        this.setState({ orderLoading: false, orderError: servicePlatform.orderError });
      }
    }
  }

  handlePatientChange = (patientData) => {
    this.setState(prev => ({ patient: { ...prev.patient, ...patientData } }));
  }

  handleSlotSelect = (slotData) => {
    const { actions } = this.props;
    actions.setSelectedServiceSlot(slotData);
  }

  handleDateSelect = (date) => {
    this.props.actions.setSelectedServiceDate(date);
  }

  validateAndProceed = () => {
    const { servicePlatform } = this.props;
    const { service, selectedSlot } = servicePlatform;
    const { patient } = this.state;

    if (this.patientFormRef) {
      const isValid = this.patientFormRef.validateAll();
      if (!isValid) return;
    }

    if (!patient.name || !patient.contact) return;

    if (service.hasSlots && (!selectedSlot || !selectedSlot.date || !selectedSlot.slotId)) return;

    this.createOrder();
  }

  createOrder = () => {
    const { actions, servicePlatform } = this.props;
    const { service, selectedPackage, selectedSlot } = servicePlatform;
    const { patient } = this.state;

    const orderParams = {
      removeAllCharges: true,
      walletOptedIn: 1,
      serviceDetails: {
        serviceId: Number(service.serviceId),
        serviceName: service.itemType || (service.serviceName || '').toUpperCase(),
        categoryId: null,
        vendorId: service.vendorId || '',
        vendorName: service.vendorName || '',
        patient: {
          name: patient.name,
          gender: patient.gender,
          contact: patient.contact,
          age: String(patient.age),
        },
        items: [selectedPackage],
      },
      promoCode: '',
      images: [],
      pdfFiles: [],
    };

    if (service.hasSlots && selectedSlot && selectedSlot.date) {
      orderParams.serviceDetails.appointment = {
        appointmentDate: selectedSlot.date,
        slotId: selectedSlot.slotId,
        slotStartTime: selectedSlot.startTime,
        slotEndTime: selectedSlot.endTime,
      };
    }

    this.setState({ orderLoading: true, orderError: '' });
    actions.createServiceOrder(orderParams);
  }

  handleBackClick = () => {
    const { params } = this.props;
    browserHistory.push(ROUTE_PATHS.SERVICE_SELECT_PACKAGE(params.serviceType));
  }

  isValid = () => {
    const { servicePlatform } = this.props;
    const { service, selectedSlot } = servicePlatform;
    const { patient } = this.state;
    const { name, contact, age } = patient;

    const isAgeValid = !age || (/^\d+$/.test(age) && Number(age) >= 1 && Number(age) <= 99);
    const hasPatient = !!(name && name.trim() && /^[0-9]{10}$/.test(contact) && isAgeValid);
    const hasSlot = !service.hasSlots || !!(selectedSlot && selectedSlot.slotId);

    return hasPatient && hasSlot;
  }

  render() {
    const { servicePlatform } = this.props;
    const { service, selectedPackage, slots, selectedSlot } = servicePlatform;
    const { patient, orderLoading, orderError, orderSuccess, orderId } = this.state;

    if (!service || !service.serviceId || !selectedPackage) {
      return <Loader />;
    }

    return (
      <div className="serviceSlotPatientWrapper">
        {orderSuccess && (
          <DocConsultSuccessModal
            orderId={String(orderId)}
            patientName={patient.name}
            doctor={{
              doctorName: selectedPackage.name || service.displayName,
              specialization: service.displayName,
              consultationFees: selectedPackage.sellingPrice || selectedPackage.mrp,
            }}
            slot={selectedSlot ? { start: selectedSlot.startTime, end: selectedSlot.endTime } : null}
            date={selectedSlot ? selectedSlot.date : ''}
            bifurcationData={null}
            consultationFees={selectedPackage.sellingPrice || selectedPackage.mrp}
            doctorLabel={staticText.packageLabel}
            onServeNext={() => { this.props.actions.cancelOrder(); browserHistory.push('/customer-info'); }}
          />
        )}
        <div className="serviceSlotPatient">
          <div className="serviceHeader">
            <h2 className="serviceTitle">{staticText.heading}</h2>
          </div>

          <div className="formsContainer">
            {service.hasSlots && (
              <div>
                {selectedPackage && selectedPackage.sessions > 1 && (
                  <div>
                    <p className="slotFirstSessionHeading">
                      {staticText.selectSlotFor}
                      <span className="slotFirstSessionHighlight">
                        {staticText.firstSession}{(selectedPackage.attributes && selectedPackage.attributes.variantsText) || staticText.sessionFallback}
                      </span>
                    </p>
                    <div className="slotRemainingNote">
                      <span className="slotRemainingNoteLabel">{staticText.noteLabel}</span>
                      {staticText.remainingNote}
                    </div>
                  </div>
                )}
                <SlotBooking
                  slots={slots}
                  onDateSelect={this.handleDateSelect}
                  onSlotSelect={this.handleSlotSelect}
                />
              </div>
            )}

            <div className="patientDetailsWrapper">
              <div className="quickStartBanner">
                <img
                  src={`${IMAGE_URL_PREFIX}service-platform/icon_Calender_Date_Orange.png`}
                  alt="calendar"
                  className="quickStartIcon"
                  width={32}
                  height={32}
                />
                <div>
                  <p className="quickStartHeading">
                    {staticText.quickStart}<span className="quickStartHighlight">{staticText.toRecovery}</span>
                  </p>
                  <p className="quickStartSubtext">{staticText.patientDetailsSubtext}</p>
                </div>
              </div>
              <PatientForm
                ref={this.setPatientFormRef}
                patient={patient}
                onChange={this.handlePatientChange}
              />
            </div>

            {orderError && (
              <p className="serviceSlotPatientError">{orderError}</p>
            )}
          </div>
        </div>

        <FooterButtons
          backBtnText={staticText.backBtn}
          backBtnClick={this.handleBackClick}
          proceedBtnText={orderLoading ? staticText.placingOrder : staticText.confirmBooking}
          proceedBtnClick={this.validateAndProceed}
          nextStateAllowed={this.isValid() && !orderLoading}
          cancelBtnText={staticText.cancelBtn}
          cancelBtnClick={() => browserHistory.push({ pathname: '/customer-info', state: { alreadyVisitedCustomerInfoPage: true } })}
        />
      </div>
    );
  }
}

ServiceSlotPatient.propTypes = {
  params: PropTypes.object.isRequired,
  servicePlatform: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};

export default connect(
  state => ({ servicePlatform: state.servicePlatform }),
  dispatch => ({ actions: bindActionCreators(actions, dispatch) })
)(ServiceSlotPatient);
